import type { ObjectLiteral } from '@prophecy/interfaces/generic';

const HINT_KEY = 'prophecy.sso.lastLoginWasSso';
const ATTEMPT_KEY = 'prophecy.sso.attempt';
const LOGOUT_KEY = 'prophecy.sso.logoutPending';
const DISABLED_WINDOW_KEY = '__prophecy-sso-auto-login-disabled';
const FLAG = '1';

const RETURN_URL_KEY = 'prophecy.sso.returnUrl';

// Local for state that outlives the tab (the hint); session for state that belongs to one tab's trip.
const storageFor = (scope: 'local' | 'session') => (scope === 'local' ? localStorage : sessionStorage);

const read = (key: string, scope: 'local' | 'session' = 'local') => {
  try {
    return storageFor(scope).getItem(key);
  } catch {
    return null;
  }
};

const setValue = (key: string, value: string, scope: 'local' | 'session' = 'local') => {
  try {
    storageFor(scope).setItem(key, value);
  } catch {
    /* a flag we cannot store just means no auto-login */
  }
};

const clearFlag = (key: string, scope: 'local' | 'session' = 'local') => {
  try {
    storageFor(scope).removeItem(key);
  } catch {
    /* nothing to clear */
  }
};

const setFlag = (key: string, scope: 'local' | 'session' = 'local') => setValue(key, FLAG, scope);
const hasFlag = (key: string, scope: 'local' | 'session' = 'local') => read(key, scope) === FLAG;

export const hasSsoLoginHint = () => hasFlag(HINT_KEY);
export const setSsoLoginHint = () => setFlag(HINT_KEY);
export const clearSsoLoginHint = () => clearFlag(HINT_KEY);

// Per tab, so a second tab booting mid-trip cannot settle this tab's attempt.
export const markSsoAttempt = () => {
  if (isSsoAutoLoginEnabled()) setFlag(ATTEMPT_KEY, 'session');
};

export const saveSsoReturnUrl = (url: string | undefined) => {
  if (url) setValue(RETURN_URL_KEY, url, 'session');
};

export const takeSsoReturnUrl = () => {
  const url = read(RETURN_URL_KEY, 'session');
  clearFlag(RETURN_URL_KEY, 'session');
  return url ?? undefined;
};

export const clearSsoState = () => {
  clearFlag(HINT_KEY);
  clearFlag(ATTEMPT_KEY, 'session');
  clearFlag(RETURN_URL_KEY, 'session');
};

export const markPendingSsoLogout = () => setFlag(LOGOUT_KEY);
export const hasPendingSsoLogout = () => hasFlag(LOGOUT_KEY);
export const clearPendingSsoLogout = () => clearFlag(LOGOUT_KEY);

// Reads the session, not a callback param, so it holds for connectors that send no success flag.
export const resolveSsoAttempt = (isLoggedIn: boolean) => {
  if (!hasFlag(ATTEMPT_KEY, 'session')) return;
  clearFlag(ATTEMPT_KEY, 'session');
  if (isLoggedIn) setSsoLoginHint();
  else clearSsoLoginHint();
};

// Expiry is when the IDP session is most likely alive, so the 401 wipe must not cost the hint.
export const preserveSsoLoginHint = (clear: () => void) => {
  const hadHint = hasSsoLoginHint();
  try {
    clear();
  } catch {
    /* blocked site data; the caller still redirects */
  }
  if (hadHint) setSsoLoginHint();
};

// On `window`, not a module variable, which can be duplicated across chunks; resolved per call so
// nothing touches `window` at import time.
const windowStore = () => window as ObjectLiteral;

export const setSsoAutoLoginDisabled = (value: unknown) => {
  windowStore()[DISABLED_WINDOW_KEY] = value === true || value === 'true';
};

// Unset means enabled, so a missing config never silently loses the feature.
export const isSsoAutoLoginEnabled = () => windowStore()[DISABLED_WINDOW_KEY] !== true;
